import { format } from 'date-fns';

/**
 * Date Formatting Utilities
 * Centralized date formatting functions used across the application
 */

/**
 * Format date as "MMM d, yyyy h:mm a"
 * @example "Jan 15, 2025 3:45 PM"
 */
export function formatDateTime(dateString?: string | null): string {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  } catch {
    return dateString;
  }
}

/**
 * Format date as "MMM d, yyyy"
 * @example "Jan 15, 2025"
 */
export function formatDate(dateString?: string | null): string {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'MMM d, yyyy');
  } catch {
    return dateString;
  }
}

/**
 * Format date as "MMM d"
 * @example "Jan 15"
 */
function formatDateShort(dateString?: string | null): string {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'MMM d');
  } catch {
    return dateString;
  }
}

/**
 * The age of a row for a fixed-width list column: `5m`, `7h`, `3d`, `12 Oct`, `12 Oct 24`.
 *
 * A list column is a measured box, and prose does not fit in one. This replaced
 * `formatVisitDate`, which rendered the same instants as "about 11 hours ago" (103px) and
 * "September 15th, 2026" (~140px) into a 92px cell — and the overflow is not clipped: it pushed
 * the whole list pane's scrollWidth past its clientWidth, which is the horizontal scrollbar under
 * /tasks?workflowStatus=enrolled (measured on prod 2026-10-29: clientWidth 990,
 * scrollWidth 1001, and the 11px was exactly `.mbt-date`'s overflow on "about 7 hours
 * ago"). The cell now also clips, so no string can do this again — but a column that
 * needs clipping to stay in its box is showing the wrong string.
 *
 * Give the exact instant to the element's `title`; this is a glance, not a record.
 */
export function formatColumnDate(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '—';
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  // Past a week the day matters more than the distance, and a year only when it is not
  // this one — "12 Oct 24" beside "12 Oct" is how a stale row stands out.
  return format(date, date.getFullYear() === new Date().getFullYear() ? 'd MMM' : 'd MMM yy');
}

/**
 * Format ISO date range for time buckets
 * @param bucketStart - Start date of the bucket (ISO string)
 * @param bucketEnd - Optional end date of the bucket (ISO string)
 * @returns Formatted date label (e.g., "Mar 15" or "Mar 15 - Mar 21")
 */
function formatBucketLabel(bucketStart: string, bucketEnd?: string): string {
  try {
    const startDate = new Date(bucketStart);
    if (bucketEnd) {
      const endDate = new Date(bucketEnd);
      if (startDate.getTime() !== endDate.getTime()) {
        return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`;
      }
    }
    return format(startDate, 'MMM d');
  } catch {
    return bucketStart;
  }
}

/**
 * The one short time-zone label for an estimated send time: the short generic name where the
 * zone has one ("CT", "ET" — boards 1/4b), else the offset ("GMT+7"), never a long regional
 * name like "Jakarta Time" (mb-frontend#4803 item 7). Empty for an unknown zone.
 */
export function shortZoneName(date: Date, timeZone: string): string {
  const zoneOf = (timeZoneName: 'shortGeneric' | 'short') => new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName })
    .formatToParts(date).find((part) => part.type === 'timeZoneName')?.value ?? '';
  try {
    const generic = zoneOf('shortGeneric');
    return generic && generic.length <= 4 ? generic : zoneOf('short');
  } catch {
    return '';
  }
}
